import { createTheme } from '@mui/material/styles';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0F1117',
      paper: '#1A1D27',
    },
    primary: {
      main: '#6C63FF',
    },
    secondary: {
      main: '#10B981',
    },
    error: {
      main: '#EF4444',
    },
    success: {
      main: '#10B981',
    },
    warning: {
      main: '#F59E0B',
    },
    text: {
      primary: '#F1F5F9',
      secondary: '#94A3B8',
    },
    divider: '#2E3247',
  },
  custom: {
    elevated: '#232636',
    hover: '#2A2D3E',
    border: '#2E3247',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0F1117',
          color: '#F1F5F9',
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: '#0F1117',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#2E3247',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#3E4257',
          },
        },
        '*': {
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: '#0F1117',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#2E3247',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#3E4257',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #6C63FF, #8B5CF6)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5B54E6, #7C4FE0)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#232636',
            color: '#F1F5F9',
            '& fieldset': {
              borderColor: '#2E3247',
            },
            '&:hover fieldset': {
              borderColor: '#6C63FF',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6C63FF',
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#64748B',
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#F1F5F9',
          backgroundColor: '#232636',
          '& fieldset': {
            borderColor: '#2E3247',
          },
          '&:hover fieldset': {
            borderColor: '#6C63FF',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#6C63FF',
          },
        },
        input: {
          '&::placeholder': {
            color: '#64748B',
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#94A3B8',
          '&.Mui-focused': {
            color: '#6C63FF',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1D27',
          border: '1px solid #2E3247',
          backgroundImage: 'none',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#232636',
          '& .MuiTableCell-head': {
            color: '#F1F5F9',
            fontWeight: 600,
            backgroundColor: '#232636',
            borderBottom: '1px solid #2E3247',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: '#232636',
            },
          },
          '& .MuiTableCell-root': {
            color: '#F1F5F9',
            borderBottom: '1px solid #2E3247',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1D27',
          border: '1px solid #2E3247',
          borderRadius: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1A1D27',
          border: '1px solid #2E3247',
          borderRadius: 16,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#10B981',
            '& + .MuiSwitch-track': {
              backgroundColor: '#10B981',
            },
          },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            color: '#94A3B8',
            '&.Mui-selected': {
              backgroundColor: '#6C63FF',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#5B54E6',
              },
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1D27',
          border: '1px solid #2E3247',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          color: '#F1F5F9',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          color: '#F1F5F9',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: '#94A3B8',
          '&.Mui-selected': {
            color: '#6C63FF',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#6C63FF',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: '#F1F5F9',
        },
        icon: {
          color: '#94A3B8',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#232636',
          border: '1px solid #2E3247',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#F1F5F9',
          '&:hover': {
            backgroundColor: '#2A2D3E',
          },
          '&.Mui-selected': {
            backgroundColor: '#2A2D3E',
            '&:hover': {
              backgroundColor: '#2A2D3E',
            },
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#F1F5F9',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: '#F1F5F9',
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: '#94A3B8',
        },
      },
    },
  },
});

export default darkTheme;
